import { Button } from '@abyss/web/ui/Button';
import { Drawer } from '@abyss/web/ui/Drawer';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getCurrentMember } from '../../utils/user.utils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { RadioGroup } from '../RadioGroup';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import { isMnrCosmosMember } from '../Utils/memberUtils/memberUtils';
import { Radio, filteredOptions } from './SortByUtil';

type Props = {
  content?: string;
  bestMatchTitle?: string;
  smartChoiceTitle?: string;
  defaultValue?: string | number | boolean;
  title: string;
  name: string;
  size: string;
  color?: string;
  icon?: string;
  variant?: string;
  model: string;
  options: Radio[];
  onApply: (value: string) => void;
};

export const SortByMobile = ({
  content,
  bestMatchTitle,
  smartChoiceTitle,
  defaultValue,
  title,
  name,
  size,
  color = '$primary1',
  icon = 'sort',
  variant = 'outline',
  model,
  options,
  onApply,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const properties = {
    drawerSize: 'fit-content',
    drawerPosition: 'bottom',
  };
  const currentMember = getCurrentMember();
  const isMnr = isMnrCosmosMember(
    currentMember?.lineOfBusiness,
    currentMember?.population
  );
  const suppressPatientReviews = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const filOptions = {
    options,
    suppressPatientReviews,
    suppressPremiumCare:
      suppressPremiumCare || suppressPremiumCareForNiceMembers,
    population: currentMember?.population,
    isMnr,
    isCns: currentMember?.isCnSMember,
    isIfp: currentMember?.isIFPMember,
  };

  const handOnApply = (value: string) => {
    if (value) {
      onApply(value);
    }
    setIsOpen(false);
  };

  const handleOnClick = () => {
    const linkName: string = title;
    const linkLocation: string = 'body:sort by';
    handleLinkAndModalTrack(linkName, linkLocation, title);
    setIsOpen(true);
  };

  return (
    <React.Fragment>
      {isOpen ? (
        <Drawer
          css={{
            'abyss-modal-content-container': {
              borderRadius: '20px 20px 0px 0px',
              padding: '$md',
              height: 'auto !important',
              '.abyss-radio-root': {
                '&:hover': {
                  backgroundColor: '$tint2',
                },
              },
            },
            'abyss-modal-close-button': {
              top: '12px',
              right: '12px',
            },
            'abyss-modal-header-container': {
              padding: '0',
              marginBottom: '$md',
            },
            'abyss-modal-content-title': {
              fontFamily: '$primary !important',
              fontSize: '$mediumText !important',
              fontWeight: '$bold !important',
              lineHeight: '24px !important',
              color: '$gray-07 !important',
              padding: '0',
            },
          }}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          position={properties.drawerPosition}
          size={properties.drawerSize}
          title={title}
        >
          <RadioGroup
            bestMatchTitle={bestMatchTitle}
            content={content}
            defaultValue={defaultValue}
            labelFontSize="16px"
            locationForAnalytics={`modal:${title}`}
            model={model}
            onApply={handOnApply}
            options={filteredOptions(filOptions)}
            smartChoiceTitle={smartChoiceTitle}
            submitBtnName={t('Sort') ?? 'Sort'}
          />
        </Drawer>
      ) : null}
      <Button
        css={{
          'abyss-button-root': {
            border: 'solid 2px $primary1',
            '&:active, &:focus, &:hover': {
              backgroundColor: '$tint3',
            },
          },
        }}
        data-auto-testid="result-sort-mobile"
        data-testid="result-sort-mobile"
        onClick={handleOnClick}
        size={size}
        variant={variant}
      >
        <Layout.Group>
          <IconMaterial color={color} icon={icon} />
          <Text color={color} fontWeight={600}>
            {name}
          </Text>
        </Layout.Group>
      </Button>
    </React.Fragment>
  );
};
